// Quarterdeck zone — milestones, okrs, timeTracking, templates
import type {
  Milestone, Objective, KeyResult, TimeEntry, TimeSummary, ProjectTemplate,
  Project,
} from './types'
import type { RequestHelper } from './client'

export function quarterdeckMethods(r: RequestHelper) {
  return {
    // Milestones
    milestones: {
      list: (projectId: string, params?: Record<string, string>) => {
        const query = params ? `?${new URLSearchParams(params).toString()}` : ''
        return r.get<{ milestones: Milestone[] }>(`/hub/projects/${projectId}/milestones${query}`)
      },
      get: (id: string) => r.get<{ milestone: Milestone }>(`/hub/milestones/${id}`),
      create: (projectId: string, data: Partial<Milestone>) => r.post<{ milestone: Milestone }>(`/hub/projects/${projectId}/milestones`, data),
      update: (id: string, data: Partial<Milestone>) => r.put<{ milestone: Milestone }>(`/hub/milestones/${id}`, data),
      delete: (id: string) => r.del<void>(`/hub/milestones/${id}`),
      dependencies: {
        list: (id: string) => r.get<{ dependencies: Milestone[] }>(`/hub/milestones/${id}/dependencies`),
        add: (id: string, dependsOnId: string) => r.post<void>(`/hub/milestones/${id}/dependencies`, { depends_on_id: dependsOnId }),
        remove: (id: string, dependsOnId: string) => r.del<void>(`/hub/milestones/${id}/dependencies/${dependsOnId}`),
      },
    },

    // OKRs
    okrs: {
      list: (projectId: string, params?: Record<string, string>) => {
        const query = params ? `?${new URLSearchParams(params).toString()}` : ''
        return r.get<{ objectives: Objective[] }>(`/hub/projects/${projectId}/objectives${query}`)
      },
      create: (projectId: string, data: Partial<Objective>) => r.post<{ objective: Objective }>(`/hub/projects/${projectId}/objectives`, data),
      update: (id: string, data: Partial<Objective>) => r.put<{ objective: Objective }>(`/hub/objectives/${id}`, data),
      delete: (id: string) => r.del<void>(`/hub/objectives/${id}`),
      addKeyResult: (objectiveId: string, data: Partial<KeyResult>) => r.post<{ key_result: KeyResult }>(`/hub/objectives/${objectiveId}/key-results`, data),
      updateKeyResult: (id: string, data: Partial<KeyResult>) => r.put<{ key_result: KeyResult }>(`/hub/key-results/${id}`, data),
      deleteKeyResult: (id: string) => r.del<void>(`/hub/key-results/${id}`),
    },

    // Time Tracking
    timeTracking: {
      list: (projectId: string, params?: Record<string, string>) => {
        const query = params ? `?${new URLSearchParams(params).toString()}` : ''
        return r.get<{ entries: TimeEntry[] }>(`/hub/projects/${projectId}/time-entries${query}`)
      },
      create: (projectId: string, data: Partial<TimeEntry>) => r.post<{ entry: TimeEntry }>(`/hub/projects/${projectId}/time-entries`, data),
      update: (id: string, data: Partial<TimeEntry>) => r.put<{ entry: TimeEntry }>(`/hub/time-entries/${id}`, data),
      delete: (id: string) => r.del<void>(`/hub/time-entries/${id}`),
      summary: (projectId: string, params?: Record<string, string>) => {
        const query = params ? `?${new URLSearchParams(params).toString()}` : ''
        return r.get<TimeSummary>(`/hub/projects/${projectId}/time-summary${query}`)
      },
      mySummary: (params?: Record<string, string>) => {
        const query = params ? `?${new URLSearchParams(params).toString()}` : ''
        return r.get<TimeSummary>(`/hub/time-entries/my-summary${query}`)
      },
    },

    // Project Templates
    templates: {
      list: () => r.get<{ templates: ProjectTemplate[] }>('/hub/templates'),
      get: (id: string) => r.get<{ template: ProjectTemplate }>(`/hub/templates/${id}`),
      create: (data: Partial<ProjectTemplate>) => r.post<{ template: ProjectTemplate }>('/hub/templates', data),
      update: (id: string, data: Partial<ProjectTemplate>) => r.put<{ template: ProjectTemplate }>(`/hub/templates/${id}`, data),
      delete: (id: string) => r.del<void>(`/hub/templates/${id}`),
      apply: (id: string, data: { project_name: string }) => r.post<{ project: Project }>(`/hub/templates/${id}/apply`, data),
    },
  }
}
